const SESSION_KEY_NAME = '_auth_k';
const SESSION_EXPIRY_NAME = '_auth_exp';
const SESSION_FP_NAME = '_auth_fp';
const SESSION_ROLE_NAME = '_auth_r';
const LOGIN_ATTEMPT_KEY = '_login_att';
const LOCKOUT_KEY = '_lockout_until';

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000;
const SESSION_DURATION = 1 * 60 * 60 * 1000;

const generateSecureRandom = () => {
  if (window.crypto?.getRandomValues) {
    const array = new Uint32Array(2);
    window.crypto.getRandomValues(array);
    return array.join('_');
  }
  return `${Math.random().toString(36).substring(2)}_${Date.now()}`;
};

export const generateSessionKey = () => {
  const timestamp = Date.now().toString(36);
  const random1 = generateSecureRandom();
  const random2 = Math.random().toString(36).substring(2, 15);
  return btoa(`${timestamp}_${random1}_${random2}`);
};

export const setSessionExpiry = (ms = SESSION_DURATION) => {
  const expiryTime = Date.now() + ms;
  sessionStorage.setItem(SESSION_EXPIRY_NAME, expiryTime.toString());
};

const generateEnhancedFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.colorDepth,
    window.screen.pixelDepth,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
  ];
  const fingerprint = components.join('|');
  return btoa(fingerprint);
};

export const checkLoginLockout = () => {
  const lockoutUntil = sessionStorage.getItem(LOCKOUT_KEY);
  if (lockoutUntil) {
    const lockoutTime = Number.parseInt(lockoutUntil);
    if (Date.now() < lockoutTime) {
      const remainingMinutes = Math.ceil((lockoutTime - Date.now()) / 60000);
      return {
        locked: true,
        remainingMinutes,
      };
    }
    sessionStorage.removeItem(LOCKOUT_KEY);
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
  }
  return { locked: false };
};

export const recordLoginAttempt = (success) => {
  if (success) {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
    sessionStorage.removeItem(LOCKOUT_KEY);
    return { allowed: true };
  }

  const attempts = Number.parseInt(
    sessionStorage.getItem(LOGIN_ATTEMPT_KEY) || '0'
  );
  const newAttempts = attempts + 1;

  if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
    const lockoutUntil = Date.now() + LOCKOUT_DURATION;
    sessionStorage.setItem(LOCKOUT_KEY, lockoutUntil.toString());
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '0');
    return {
      allowed: false,
      locked: true,
      remainingMinutes: Math.ceil(LOCKOUT_DURATION / 60000),
    };
  }

  sessionStorage.setItem(LOGIN_ATTEMPT_KEY, newAttempts.toString());
  return {
    allowed: true,
    attemptsRemaining: MAX_LOGIN_ATTEMPTS - newAttempts,
  };
};

export const validateSession = () => {
  const sessionKey = sessionStorage.getItem(SESSION_KEY_NAME);
  const sessionExpiry = sessionStorage.getItem(SESSION_EXPIRY_NAME);
  const browserFingerprint = sessionStorage.getItem(SESSION_FP_NAME);
  const userRole = sessionStorage.getItem(SESSION_ROLE_NAME);

  if (
    !sessionKey ||
    !sessionExpiry ||
    !browserFingerprint ||
    userRole !== 'admin'
  ) {
    return false;
  }

  const currentTime = Date.now();
  if (currentTime > Number.parseInt(sessionExpiry)) {
    clearSession();
    return false;
  }

  const currentFingerprint = generateEnhancedFingerprint();
  if (currentFingerprint !== browserFingerprint) {
    clearSession();
    return false;
  }

  return true;
};

export const clearSession = () => {
  sessionStorage.removeItem(SESSION_KEY_NAME);
  sessionStorage.removeItem(SESSION_EXPIRY_NAME);
  sessionStorage.removeItem(SESSION_FP_NAME);
  sessionStorage.removeItem(SESSION_ROLE_NAME);
  sessionStorage.removeItem('adminSessionKey');
  sessionStorage.removeItem('loginTime');
  sessionStorage.removeItem('userRole');
  sessionStorage.removeItem('sessionExpiry');
  sessionStorage.removeItem('browserFingerprint');
  sessionStorage.removeItem('id');
};

export const refreshSession = (ms = SESSION_DURATION) => {
  if (validateSession()) {
    const newExpiryTime = Date.now() + ms;
    sessionStorage.setItem(SESSION_EXPIRY_NAME, newExpiryTime.toString());
    return true;
  }
  return false;
};

export const setLoginSession = () => {
  const sessionKey = generateSessionKey();
  const browserFingerprint = generateEnhancedFingerprint();

  sessionStorage.setItem(SESSION_KEY_NAME, sessionKey);
  sessionStorage.setItem(SESSION_ROLE_NAME, 'admin');
  sessionStorage.setItem(SESSION_FP_NAME, browserFingerprint);
  setSessionExpiry();

  return sessionKey;
};
